import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Edit, 
  Trash2, 
  Calendar,
  Building2,
  Users
} from "lucide-react";
import SaudiRiyal from "@/components/shared/saudi-riyal";
import { PropertyType } from "@/mockData/rooms";

interface PropertyTypeViewProps {
  isOpen: boolean;
  onClose: () => void;
  propertyType: PropertyType | null;
  onEdit: (propertyType: PropertyType) => void;
  onDelete: (propertyType: PropertyType) => void;
  propertyCount?: number;
}

export function PropertyTypeView({ 
  isOpen, 
  onClose, 
  propertyType, 
  onEdit, 
  onDelete,
  propertyCount = 0 
}: PropertyTypeViewProps) {
  const { t } = useTranslation();

  if (!propertyType) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="text-3xl">{propertyType.icon}</span>
            <div>
              <h2 className="text-xl font-bold">{propertyType.name_en}</h2>
              <p className="text-sm text-muted-foreground">{propertyType.name_ar}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t("propertyTypeInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("englishName")}</h4>
                  <p className="text-lg">{propertyType.name_en}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("arabicName")}</h4>
                  <p className="text-lg">{propertyType.name_ar}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("englishDescription")}</h4>
                <p className="text-sm">{propertyType.description_en}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("arabicDescription")}</h4>
                <p className="text-sm">{propertyType.description_ar}</p>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t("statistics")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{propertyCount}</div>
                  <div className="text-sm text-muted-foreground">{t("properties")}</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">0</div>
                  <div className="text-sm text-muted-foreground">{t("activeBookings")}</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">
                    <SaudiRiyal amount={0} variant="bold_new" className="text-2xl" />
                  </div>
                  <div className="text-sm text-muted-foreground">{t("totalRevenue")}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t("metadata")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("createdAt")}</h4>
                  <p className="text-sm">{formatDate(propertyType.created_at)}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-1">{t("lastUpdated")}</h4>
                  <p className="text-sm">{formatDate(propertyType.updated_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onEdit(propertyType)}>
              <Edit className="h-4 w-4 mr-2" />
              {t("editPropertyType")}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => onDelete(propertyType)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t("deletePropertyType")}
            </Button>
          </div>
          <Button variant="outline" onClick={onClose}>
            {t("close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
